// src/components/Navbar.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './Navbar.css';
import { useNav } from './NavContext';
import logo from '../assets/images/icon.png';
import { clearAllSessions, getActiveAccountId, getActiveSession, loadSessions, removeSession, saveSessions, setActiveSession } from '../utils/accountSessions';

const Navbar = () => {
  const { links } = useNav();
  const [menuOpen, setMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [accountsOpen, setAccountsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const navRef = useRef(null);
  const settingsRef = useRef(null);
  const accountsRef = useRef(null);

  const [sessions, setSessions] = useState(() => loadSessions());
  const [activeSession, setActiveSessionState] = useState(() => getActiveSession());

  const API_URL = process.env.REACT_APP_API_URL;
  const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
  const isDoctor = !!(activeSession || localStorage.getItem('userInfo'));
  const isAssistant = userInfo.role === 'assistant';
  const isAdmin = userInfo.role === 'admin';
  const isTest = userInfo.role === 'test';

  useEffect(() => {
    const onChange = () => {
      setSessions(loadSessions());
      setActiveSessionState(getActiveSession());
    };
    window.addEventListener('accountSessionsChanged', onChange);
    return () => window.removeEventListener('accountSessionsChanged', onChange);
  }, []);

  useEffect(() => {
    const bootstrap = async () => {
      if (!API_URL) return;

      try {
        const res = await axios.get(API_URL + 'auth/accounts');
        const accounts = res?.data?.accounts;
        if (!Array.isArray(accounts)) return;

        const nextSessions = accounts.map((a) => ({
          id: a.id,
          role: a.role,
          user: a.user,
          doctor: a.doctor,
          addedAt: new Date().toISOString(),
        }));

        saveSessions(nextSessions);

        const activeId = getActiveAccountId();
        const activeStillValid = activeId && nextSessions.some((s) => String(s.id) === String(activeId));
        if (!activeStillValid && nextSessions[0]?.id) {
          setActiveSession(nextSessions[0].id);
        }
      } catch (_) {
      }
    };

    bootstrap();
  }, [API_URL]);


  const handleLogout = async () => {
    try {
      if (API_URL) {
        await axios.post(API_URL + 'auth/logout-selected', {});
      }
    } catch (e) {
      // Ignore API errors during logout; proceed to clear local state
      // console.error('Logout error', e);
    } finally {
      if (activeSession?.id) {
        removeSession(activeSession.id);
      } else {
        localStorage.removeItem('userInfo');
      }
      if (window.AndroidInterface && window.AndroidInterface.onLogout) {
        window.AndroidInterface.onLogout();
      }
      try {
        const next = getActiveSession();
        if (!next) {
          window.location.assign('/control/login');
          return;
        }

        if (next.role === 'admin') {
          window.location.assign('/control/doctor-list');
        } else if (next.role === 'test') {
          window.location.assign('/control/test-information');
        } else {
          window.location.assign('/control/appointments');
        }
      } catch (_) {
        const next = getActiveSession();
        if (!next) {
          navigate('/control/login');
        } else if (next.role === 'admin') {
          navigate('/control/doctor-list');
        } else if (next.role === 'test') {
          navigate('/control/test-information');
        } else {
          navigate('/control/appointments');
        }
      }
    }
  };

  const handleLogoutAll = async () => {
    try {
      if (API_URL) {
        await axios.post(API_URL + 'auth/logout-all', {}, {
          headers: { 'X-Logout-All': '1' },
        });
      }
    } catch (_) {
    } finally {
      clearAllSessions();
      if (window.AndroidInterface && window.AndroidInterface.onLogout) {
        window.AndroidInterface.onLogout();
      }
      try {
        window.location.assign('/control/login');
      } catch (_) {
        navigate('/control/login');
      }
    }
  };

  const closeMenu = () => {
    setMenuOpen(false);
  };


  const toggleSettings = () => {
    setSettingsOpen(!settingsOpen);
  };

  const toggleAccounts = () => {
    setAccountsOpen(!accountsOpen);
  };

  const closeSettings = () => {
    setSettingsOpen(false);
  };

  const closeAccounts = () => {
    setAccountsOpen(false);
  };

  const accountLabel = (session) => {
    if (!session) return 'Account';
    const role = session.role || session.user?.role || '';
    const base = session.doctor?.full_name || session.user?.name || session.user?.email || `#${session.id}`;
    return role ? `${base} (${role})` : base;
  };

  const switchToAccount = (session) => {
    if (!session?.id) return;
    setActiveSession(session.id);
    closeAccounts();
    closeMenu();

    const role = session.role;
    if (role === 'admin') {
      navigate('/control/doctor-list');
    } else if (role === 'test') {
      navigate('/control/test-information');
    } else {
      navigate('/control/appointments');
    }
  };

  const handleLinkClick = () => {
    closeMenu();
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        closeMenu();
      }
      if (settingsRef.current && !settingsRef.current.contains(event.target)) {
        closeSettings();
      }
      if (accountsRef.current && !accountsRef.current.contains(event.target)) {
        closeAccounts();
      }
    };

    if (menuOpen || settingsOpen || accountsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen, accountsOpen, settingsOpen]);
  return (
    <header className="navbar rtl" ref={navRef}>
      <div className="container">
        <button
          className="back-button-mobile"
          onClick={() => navigate(-1)}
          aria-label="Go Back"
        >
          <i className="fas fa-arrow-left"></i>
        </button>
        <div className="logo">
          <Link to="#">
            <img src={logo} className="logo-icon" alt="Logo" />
            {isTest ? (
                // Role = test: Only show Profile (points to TestInformation)
                <span className="logo-text">Bro</span>
              ) : (
                <span className="logo-text">clinic</span>
              )}
          </Link>
        </div>
        <button
          className="menu-toggle"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle Menu"
        >
          <i className="fas fa-bars"></i>
        </button>
        <nav className={`menu ${menuOpen ? 'open' : ''}`}>
          {!isTest && links.map((link) => (
            <Link key={link.path} to={link.path} onClick={handleLinkClick}>
              <i className="fas fa-home me-2"></i>
              {link.name}
            </Link>
          ))}
          {isDoctor && (
            <div className="nav-dropdown" ref={accountsRef}>
              <button
                className="btn btn-link nav-dropdown-toggle"
                onClick={(e) => { e.preventDefault(); toggleAccounts(); }}
                aria-expanded={accountsOpen}
                aria-haspopup="true"
              >
                <i className="fas fa-user-circle me-2"></i>
                {accountLabel(activeSession)}
                <i className={`fas fa-chevron-${accountsOpen ? 'up' : 'down'} ms-1`}></i>
              </button>
              {accountsOpen && (
                <div className="nav-dropdown-menu">
                  {sessions.length > 0 && sessions.map((s) => (
                    <button
                      key={String(s.id)}
                      type="button"
                      className="dropdown-button-link"
                      onClick={(e) => {
                        e.preventDefault();
                        switchToAccount(s);
                      }}
                      style={{ opacity: String(activeSession?.id) === String(s.id) ? 1 : 0.95 }}
                    >
                      <i className={`fas ${String(activeSession?.id) === String(s.id) ? 'fa-check-circle' : 'fa-user'} me-2`}></i>
                      {accountLabel(s)}
                    </button>
                  ))}

                  <Link
                    to="/control/login?add=1"
                    onClick={() => {
                      handleLinkClick();
                      closeAccounts();
                    }}
                  >
                    <i className="fas fa-user-plus me-2"></i>
                    Add account
                  </Link>

                  <button
                    type="button"
                    className="dropdown-button-link"
                    onClick={(e) => {
                      e.preventDefault();
                      handleLogout();
                      handleLinkClick();
                      closeAccounts();
                    }}
                  >
                    <i className="fas fa-sign-out-alt me-2"></i>
                    Logout current
                  </button>

                  <button
                    type="button"
                    className="dropdown-button-link"
                    onClick={(e) => {
                      e.preventDefault();
                      handleLogoutAll();
                      handleLinkClick();
                      closeAccounts();
                    }}
                  >
                    <i className="fas fa-door-open me-2"></i>
                    Logout all
                  </button>
                </div>
              )}
            </div>
          )}
          {isDoctor && (
            <>
              {isTest ? (
                // Role = test: Only show Profile (points to TestInformation)
                <Link key="/control/test-information" to="/control/test-information" onClick={handleLinkClick}>
                  Profile
                </Link>
              ) : (
                <>
                  {!isAdmin && (
                    <>
                      <Link key="/control/appointments" to="/control/appointments" onClick={handleLinkClick}>
                        <i className="fas fa-calendar-alt me-2"></i>
                        Appointments
                      </Link>
                      <Link key="/control/patients" to="/control/patients" onClick={handleLinkClick}>
                        <i className="fas fa-users me-2"></i>
                        Patients
                      </Link>
                      {!isAssistant && (
                        <div className="nav-dropdown" ref={settingsRef}>
                          <button
                            className="btn btn-link nav-dropdown-toggle"
                            onClick={(e) => { e.preventDefault(); toggleSettings(); }}
                            aria-expanded={settingsOpen}
                            aria-haspopup="true"
                          >
                            <i className="fas fa-user-md me-2"></i>
                            Settings
                            <i className={`fas fa-chevron-${settingsOpen ? 'up' : 'down'} ms-1`}></i>
                          </button>
                          {settingsOpen && (
                            <div className="nav-dropdown-menu">
                              <Link to="/control/doctor-information" onClick={() => { handleLinkClick(); closeSettings(); }}>
                                <i className="fas fa-id-card me-2"></i>
                                Doctor Profile
                              </Link>
                              <Link to="/control/stats" onClick={() => { handleLinkClick(); closeSettings(); }}>
                                <i className="fas fa-chart-line me-2"></i>
                                Statistics
                              </Link>
                              <Link to="/control/notes-search" onClick={() => { handleLinkClick(); closeSettings(); }}>
                                <i className="fas fa-search me-2"></i>
                                Search Visits
                              </Link>
                              <Link to="/control/admin/assistants" onClick={() => { handleLinkClick(); closeSettings(); }}>
                                <i className="fas fa-user-tie me-2"></i>
                                Assistants
                              </Link>
                              <Link to="/control/print-settings" onClick={() => { handleLinkClick(); closeSettings(); }}>
                                <i className="fas fa-print me-2"></i>
                                Print Settings
                              </Link>
                              <Link to="/control/message-templates" onClick={() => { handleLinkClick(); closeSettings(); }}>
                                <i className="fas fa-file-alt me-2"></i>
                                Message Templates
                              </Link>
                              <Link to="/control/messages" onClick={() => { handleLinkClick(); closeSettings(); }}>
                                <i className="fas fa-comments me-2"></i>
                                Messages
                              </Link>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}
          {!isDoctor ? (
            <Link key="/control/login" to="/control/login" onClick={handleLinkClick}>
              <i className="fas fa-sign-in-alt me-2"></i>
              Login
            </Link>
          ) : null}
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
